import { MenuItem } from "prosemirror-menu";
import type { EditorState } from "prosemirror-state";

import { toolbarPanelPluginKey } from "../plugins/toolbar-panel";

import { t } from "i18n/i18n";
import { ADD_PLUS_ICON_PATHS, CROSS_REMOVE_ICON_PATHS, ICON_SIZE } from "mds/utils/toolbar-icons";

type StrokeIcon = { dom: Node };

const SVG_NS = "http://www.w3.org/2000/svg";

function createStrokeIcon(paths: readonly string[]): StrokeIcon {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${ICON_SIZE} ${ICON_SIZE}`);
  svg.setAttribute("width", String(ICON_SIZE));
  svg.setAttribute("height", String(ICON_SIZE));
  svg.setAttribute("fill", "none");
  for (const d of paths) {
    const pathEl = document.createElementNS(SVG_NS, "path");
    pathEl.setAttribute("d", d);
    pathEl.setAttribute("stroke", "currentColor");
    pathEl.setAttribute("stroke-linecap", "round");
    pathEl.setAttribute("stroke-linejoin", "round");
    pathEl.setAttribute("stroke-miterlimit", "10");
    svg.appendChild(pathEl);
  }
  return { dom: svg };
}

const expandPanelIcon = createStrokeIcon(ADD_PLUS_ICON_PATHS);
const collapsePanelIcon = createStrokeIcon(CROSS_REMOVE_ICON_PATHS);

/**
 * Creates a toolbar MenuItem that navigates from sourcePanel to targetPanel.
 * Visible only when sourcePanel is active.
 */
export function createGoToPanelItem(
  targetPanel: number,
  sourcePanel: number,
  icon: StrokeIcon,
  title: string,
): MenuItem {
  return new MenuItem({
    title,
    icon, // prosemirror-menu's path spec renders filled shapes only; stroke attributes require a DOM node
    select(state: EditorState) {
      return (toolbarPanelPluginKey.getState(state)?.activePanel ?? 0) === sourcePanel;
    },
    run(state, dispatch) {
      dispatch(state.tr.setMeta(toolbarPanelPluginKey, { activePanel: targetPanel }));
    },
  });
}

export const expandToPanelOneItem = createGoToPanelItem(
  1,
  0,
  expandPanelIcon,
  t("worksheet.rich_text_editor.expand_toolbar"),
);

export const collapseToPanelZeroItem = createGoToPanelItem(
  0,
  1,
  collapsePanelIcon,
  t("worksheet.rich_text_editor.collapse_toolbar"),
);
